import React, { useEffect, useState } from 'react';
import Base from '../Base';
import Title from '../../components/common/Title';
import { useTranslation } from 'react-i18next';
import { fetchUserInfo, apiRequest } from '../../utils';
import Card from '../../components/elements/Card';
import Image from '../../components/images/Image';
import Icon from '../../components/common/Icon';
import SameLine from '../../components/css/SameLine';
import Back from '../../components/common/Back';
import Center from '../../components/css/Center';
import Button from '../../components/elements/Button';
import Loading from '../../components/common/Loading';
import Toast from '../../components/forms/Toast';

const fallbackMissionImage = require('./../../assets/images/fallback/mission.png');

const PLATFORMS = [
  { name: 'instagram', label: 'Instagram' },
  { name: 'tiktok', label: 'TikTok', icon: 'tik-tok' },
  { name: 'facebook', label: 'Facebook' },
  { name: 'youtube', label: 'YouTube' },
  { name: 'linkedin', label: 'LinkedIn' },
];

const detectPlatform = (url) => {
  if (!url) return null;
  const u = url.toLowerCase();
  if (/instagram\.|instagr\.am/.test(u)) return PLATFORMS[0];
  if (/tiktok\./.test(u)) return PLATFORMS[1];
  if (/facebook\.|fb\.watch|fb\.com/.test(u)) return PLATFORMS[2];
  if (/youtube\.|youtu\.be/.test(u)) return PLATFORMS[3];
  if (/linkedin\./.test(u)) return PLATFORMS[4];
  return null;
};

const StatusPill = ({ submission }) => {
  const { t } = useTranslation();
  if (!submission) {
    return (
      <span className="status-pill status-pill--todo">
        <Icon name="circle" /> {t('missionStatusTodo')}
      </span>
    );
  }
  if (submission.is_approved) {
    return (
      <span className="status-pill status-pill--approved">
        <Icon name="check-circle" /> {t('missionStatusApproved')}
      </span>
    );
  }
  return (
    <span className="status-pill status-pill--pending">
      <Icon name="duration-alt" /> {t('missionStatusPending')}
    </span>
  );
};

const SocialMissionPage = () => {
  const { t } = useTranslation();
  const [mission, setMission] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [link, setLink] = useState('');

  const loadMission = () => {
    fetchUserInfo(
      '/social_mission',
      (data) => {
        setMission(data.mission);
        setSubmission(data.submission);
        setUserInfo(data);
        setLink(data?.submission?.caption || '');
      },
      setLoading,
      setError,
      []
    );
  };

  useEffect(() => {
    loadMission();
  }, []);

  const submitLink = async () => {
    if (!mission || !link.trim() || submitting) return;
    try {
      setSubmitting(true);
      setError(null);
      await apiRequest('POST', '/submit_social_mission', { link: link.trim() });
      setError('uploadSuccess');
      setShowToast(true);
      loadMission();
    } catch (err) {
      setError(err.message || 'genericError');
      setShowToast(true);
    } finally {
      setSubmitting(false);
    }
  };

  const deleteSubmission = async () => {
    if (!submission || deleting) return;
    if (!window.confirm(t('confirmDeleteSubmission'))) return;

    try {
      setDeleting(true);
      setError(null);
      await apiRequest('DELETE', '/social-submissions/delete', {
        submission_id: submission.submission_id,
      });
      setSubmission(null);
      setLink('');
      setError('submissionDeleted');
      setShowToast(true);
    } catch (err) {
      setError(err.message || 'genericError');
      setShowToast(true);
    } finally {
      setDeleting(false);
    }
  };

  const earned = submission && submission.is_approved;
  const canDelete = !!submission && sessionStorage.getItem('otherTeamId') === userInfo?.team_id;
  const linkUrl = submission?.social_media_url || submission?.caption;
  const submittedPlatform = detectPlatform(linkUrl);

  return (
    <Base activeNav="home">
      <Card>
        <Back to="/missions" />

        {mission && (
          <>
            <Center>
              <Image
                className="mb-3 circle"
                size="12rem"
                url={mission.photo_url || fallbackMissionImage}
              />
            </Center>

            <Title my="0" className="mb-2">
              {mission?.mission_name || t('mission')}
            </Title>

            <div className="d-flex justify-content-center align-items-center gap-2 mb-3" style={{ flexWrap: 'wrap' }}>
              <StatusPill submission={submission} />
              <span className={`points-chip ${earned ? 'points-chip--earned' : ''}`} style={{ marginTop: 0 }}>
                <Icon name="star" />
                {earned ? '+' : ''}{mission?.points || 0} {t('pts')}
              </span>
            </div>

            <div className="mb-3">{mission.description}</div>

            <div className="social-tip mb-3">
              {/* "hastag" is spelled that way in the uicons pack we load — not a typo of ours. */}
              <Icon name="hastag" theme="primary" className="me-2" />
              <span><strong>{t('tagUs')}</strong> {t('tagUsInYourPost')}</span>
            </div>

            {(mission?.reference_video || mission?.reference_url) && (
              <p className="mb-3">
                <Icon name="bulb" theme="primary" className="me-2" />
                <strong>{t('inspiration')}:</strong>{' '}
                <a
                  href={mission.reference_video || mission.reference_url}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('referenceVideo')}
                </a>
              </p>
            )}

            <div className="collab-callout mb-3">
              <div className="collab-callout__icon">
                <Icon name="users" theme="light" />
              </div>
              <div className="collab-callout__body">
                <div className="collab-callout__title">{t('addUsAsCollaboratorTitle')}</div>
                <div className="collab-callout__text">{t('addUsAsCollaboratorBody')}</div>
              </div>
            </div>

            <hr />

            {!submission ? (
              <>
                <label htmlFor="social-link-input" className="mb-2 fw-semibold d-block text-start">
                  {t('socialLink')}
                </label>
                <input
                  id="social-link-input"
                  type="url"
                  value={link}
                  onChange={(e) => setLink(e.target.value)}
                  placeholder="https://"
                  className="form-control mb-2 social-link-input"
                />
                <div className="platforms-row mb-3">
                  {PLATFORMS.map((p) => (
                    <span key={p.name} className={`platform-chip platform-chip--${p.name}`} title={p.label}>
                      <Icon name={p.icon || p.name} pack="brands" />
                      <span>{p.label}</span>
                    </span>
                  ))}
                </div>
                <Button onClick={submitLink} disabled={!link.trim() || submitting}>
                  <Icon name="link" theme="light" className="me-1" />
                  {submitting ? t('loading') : t('submitLink')}
                </Button>
              </>
            ) : (
              <>
                <Title my="0" className="mb-2">
                  <Icon name="link" theme="primary" style={{ fontSize: '1.4rem' }} className="me-2" />
                  {t('yourSubmission')}
                </Title>

                {linkUrl && (
                  <div className="submission-link-card mb-3">
                    {submittedPlatform && (
                      <span className={`platform-badge platform-badge--${submittedPlatform.name} submission-link-card__badge`} title={submittedPlatform.label}>
                        <Icon name={submittedPlatform.icon || submittedPlatform.name} pack="brands" theme="light" />
                      </span>
                    )}
                    <a href={linkUrl} target="_blank" rel="noreferrer" style={{ wordBreak: 'break-all' }}>
                      {linkUrl}
                    </a>
                  </div>
                )}

                <div className="mt-3">
                  <div className="text-secondary mb-1" style={{ fontSize: '0.85rem' }}>{t('uploadedBy')}</div>
                  <div style={{ maxWidth: '20rem', margin: '0 auto' }}>
                    <Card to={`/u/${submission.uploaded_by_id}`}>
                      <SameLine className="row">
                        <div className="col-4">
                          <Image url={submission.uploaded_by_photo_url} size="4rem" className="circle" />
                        </div>
                        <div className="col-8 text-start">
                          <div>{submission.uploaded_by_name}</div>
                          {submission.uploaded_at && (
                            <div className="text-secondary" style={{ fontSize: '0.85rem' }}>
                              {new Date(submission.uploaded_at).toLocaleString(undefined, {
                                dateStyle: 'medium',
                                timeStyle: 'short',
                              })}
                            </div>
                          )}
                        </div>
                      </SameLine>
                    </Card>
                  </div>
                </div>
              </>
            )}

            {canDelete && (
              <div className="mt-3">
                <Button
                  theme="danger"
                  onClick={deleteSubmission}
                  disabled={deleting}
                  styles={{ margin: '0 auto' }}
                >
                  <Icon name="trash" theme="light" className="me-2" />
                  {deleting ? t('deleting') : t('deleteSubmission')}
                </Button>
              </div>
            )}
          </>
        )}

        {loading && <Loading />}
        <Toast showToast={showToast} setShowToast={setShowToast} message={t(error)} />
      </Card>
    </Base>
  );
};

export default SocialMissionPage;
